import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GradientButton } from "@/components/ui/gradient-button";
import { motion, AnimatePresence } from "framer-motion";
import logoAzul from "@/assets/logo-azul.webp";
import logoVerde from "@/assets/logo-verde.webp";

const navLinks = [
  { label: "O que está incluso", href: "#incluso", external: false },
  { label: "Como funciona", href: "#como-funciona", external: false },
  { label: "Benefícios", href: "#beneficios", external: false },
  { label: "Comparativo", href: "#comparativo", external: false },
  { label: "FAQ", href: "#faq", external: false },
  { label: "Blog", href: "/blog", external: false, isRoute: true },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if scrolled past threshold
      setIsScrolled(currentScrollY > 10);
      
      // Show header when at top, scrolling up, or mobile menu is open
      if (currentScrollY < 50 || isMobileMenuOpen) {
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past threshold
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isMobileMenuOpen]);

  const scrollToSection = (href: string) => {
    setIsMobileMenuOpen(false);
    
    if (!isHomePage) {
      // Navigate to home first, then scroll after a brief delay
      navigate("/");
      setTimeout(() => {
        const element = document.querySelector(href);
        element?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }
    
    const element = document.querySelector(href);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      } ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border"
          : "bg-background/80 backdrop-blur-sm border-b border-border/30"
      }`}
    >
      <nav className="container flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" onClick={handleLogoClick} className="flex items-center">
          <img
            src={logoAzul}
            alt="Aceena - Consultoria em Carro por Assinatura"
            width={120}
            height={32}
            className="h-7 lg:h-9 w-auto transition-all duration-300"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
        {navLinks.map((link) => {
          const linkClass = `transition-colors text-sm font-medium ${
            isScrolled
              ? "text-muted-foreground hover:text-foreground"
              : "text-foreground/70 hover:text-foreground"
          }`;

          if (link.external) {
            return (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                {link.label}
              </a>
            );
          }

          if ((link as any).isRoute) {
            return (
              <Link key={link.href} to={link.href} className={linkClass}>
                {link.label}
              </Link>
            );
          }

          return (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className={linkClass}
            >
              {link.label}
            </button>
          );
        })}
          <GradientButton
            variant="primary"
            size="sm"
            onClick={() => {
              if (typeof window !== 'undefined' && (window as any).fbq) {
                (window as any).fbq('track', 'Lead');
              }
              window.open("https://wa.me/5511964774179?text=Olá! Gostaria de falar com um especialista sobre carro por assinatura.", "_blank");
            }}
          >
            Falar com um especialista
          </GradientButton>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden p-2 rounded-lg transition-colors ${
            isScrolled
              ? "hover:bg-muted"
              : "hover:bg-muted/50"
          }`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Menu"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-foreground" />
          ) : (
            <Menu className="w-6 h-6 text-foreground" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden"
          >
            <div className="container py-4 flex flex-col gap-2">
              {navLinks.map((link) => {
                const mobileClass = "text-left py-3 px-4 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors";

                if (link.external) {
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${mobileClass} block`}
                    >
                      {link.label}
                    </a>
                  );
                }

                if ((link as any).isRoute) {
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`${mobileClass} block`}
                    >
                      {link.label}
                    </Link>
                  );
                }

                return (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className={mobileClass}
                  >
                    {link.label}
                  </button>
                );
              })}
              <GradientButton
                variant="primary"
                className="mt-2"
                onClick={() => {
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    (window as any).fbq('track', 'Lead');
                  }
                  window.open("https://wa.me/5511964774179?text=Olá! Gostaria de falar com um especialista sobre carro por assinatura.", "_blank");
                }}
              >
                Falar com um especialista
              </GradientButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
